// Перенос 1:1 из Launch Pipeline (frontend/src/shared/lib/calculations/nicheCriteria.ts).
// Маппинг niche-снимка (PLP-24) на 14 niche-критериев балльной оценки GO/COND/NO-GO (PLP-23).
// Зеркало backend `app/core/calculations/niche_criteria.py`. Чистая функция, no I/O.
//
// Пороги/баллы — из утверждённого проекта docs/mf/PLP-23-niche-criteria-draft.md (Спрос 7 +
// Конкуренция 7, шкала 0/1/2), именованные константы в ./constants. Для каждого критерия берётся
// поле niche-снимка → балл 0/1/2 по порогам → нормировка в rawScore 0..1 (÷ максимум критерия) →
// NicheCriterionInput для ядра evaluateGoNoGoScore. Поле отсутствует/не число → критерий НЕ
// добавляется (частичная оценка по доступным, NO FALLBACKS). Активная таблица — 12 критериев (1–12);
// критерии 13–14 (число продавцов N, свежесть) — follow-up (пороги проекта не доопределены).
// Критерии 9–11 читают агрегаты конкурентов из asinMetrics (top5ClickShare/mainCompetitorReviews/
// mainCompetitorRating), которые снимок теперь несёт (их считает бэкенд-шлюз ниш).

import {
  NICHE_ACTUAL_SALES_HIGH,
  NICHE_ACTUAL_SALES_MID,
  NICHE_CONVERSION_GOOD_HIGH,
  NICHE_CONVERSION_GOOD_LOW,
  NICHE_CONVERSION_OVERHEATED,
  NICHE_CRITERION_MULTIPLIER,
  NICHE_DEMAND_GROWTH90_FLAT,
  NICHE_DEMAND_GROWTH90_HIGH,
  NICHE_DEMAND_VOLUME_HIGH,
  NICHE_DEMAND_VOLUME_MID,
  NICHE_MAX_POINT_BINARY,
  NICHE_MAX_POINT_TERNARY,
  NICHE_PRICE_MAX,
  NICHE_PRICE_MIN,
  NICHE_RATING_MID,
  NICHE_RATING_WEAK,
  NICHE_REAL_DEMAND_HIGH,
  NICHE_REAL_DEMAND_MID,
  NICHE_RETURN_LOW,
  NICHE_RETURN_MID,
  NICHE_REVIEWS_LOW,
  NICHE_REVIEWS_MID,
  NICHE_SATURATION_LOW,
  NICHE_SATURATION_MID,
  NICHE_TOP5_CONCENTRATION_LOW,
  NICHE_TOP5_CONCENTRATION_MID,
  NICHE_YOY_TREND_MIN,
} from './constants';
import { type NicheCriterionInput } from './goNogo';

// niche-критерии живут только в блоках Спрос/Конкуренция (подмножество ScoringBlock ядра).
type NicheBlock = NicheCriterionInput['block'];

/** Поля niche-снимка (PLP-24), которые читают критерии. Все опциональны — снимок толерантен к
 *  пропускам (POE может не вернуть часть полей). Доли для *Rate/*Growth: 0.15 = 15%. */
export interface NicheDataInput {
  searchVolumeT90?: number | null;
  searchConversionRate?: number | null;
  searchVolumeGrowthT90?: number | null;
  searchVolumeGrowthT360?: number | null;
  maximumUnitsSoldT360?: number | null;
  avgPrice?: number | null;
  productCount?: number | null;
  returnRateT360?: number | null;
  // Агрегаты конкурентов из asinMetrics (критерии 9–11). top5ClickShare — доля 0..1.
  top5ClickShare?: number | null;
  mainCompetitorReviews?: number | null;
  mainCompetitorRating?: number | null;
}

function num(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

// --- Балл 0/1/2 по порогам проекта; null — обязательного поля нет (критерий пропускается). --- //

function scoreDemandVolume(nd: NicheDataInput): number | null {
  const sv = num(nd.searchVolumeT90);
  if (sv === null) return null;
  if (sv >= NICHE_DEMAND_VOLUME_HIGH) return 2;
  return sv >= NICHE_DEMAND_VOLUME_MID ? 1 : 0;
}

function scoreRealDemand(nd: NicheDataInput): number | null {
  const sv = num(nd.searchVolumeT90);
  const cr = num(nd.searchConversionRate);
  if (sv === null || cr === null) return null;
  const demand = sv * cr;
  if (demand >= NICHE_REAL_DEMAND_HIGH) return 2;
  return demand >= NICHE_REAL_DEMAND_MID ? 1 : 0;
}

function scoreConversion(nd: NicheDataInput): number | null {
  const cr = num(nd.searchConversionRate);
  if (cr === null) return null;
  if (cr >= NICHE_CONVERSION_GOOD_LOW && cr <= NICHE_CONVERSION_GOOD_HIGH) return 2;
  // <3% или 10–15% → 1; >15% → 0 (перегрета/спам).
  return cr > NICHE_CONVERSION_OVERHEATED ? 0 : 1;
}

function scoreDemandGrowth90(nd: NicheDataInput): number | null {
  const g = num(nd.searchVolumeGrowthT90);
  if (g === null) return null;
  if (g > NICHE_DEMAND_GROWTH90_HIGH) return 2;
  return g < NICHE_DEMAND_GROWTH90_FLAT ? 0 : 1;
}

function scoreYoyTrend(nd: NicheDataInput): number | null {
  const g = num(nd.searchVolumeGrowthT360);
  if (g === null) return null;
  return g > NICHE_YOY_TREND_MIN ? 1 : 0;
}

function scoreActualSales(nd: NicheDataInput): number | null {
  const units = num(nd.maximumUnitsSoldT360);
  if (units === null) return null;
  if (units >= NICHE_ACTUAL_SALES_HIGH) return 2;
  return units >= NICHE_ACTUAL_SALES_MID ? 1 : 0;
}

function scorePriceLevel(nd: NicheDataInput): number | null {
  const price = num(nd.avgPrice);
  if (price === null) return null;
  return price >= NICHE_PRICE_MIN && price <= NICHE_PRICE_MAX ? 1 : 0;
}

function scoreSaturation(nd: NicheDataInput): number | null {
  const count = num(nd.productCount);
  if (count === null) return null;
  if (count < NICHE_SATURATION_LOW) return 2;
  return count <= NICHE_SATURATION_MID ? 1 : 0;
}

function scoreTop5Concentration(nd: NicheDataInput): number | null {
  const share = num(nd.top5ClickShare);
  if (share === null) return null;
  if (share < NICHE_TOP5_CONCENTRATION_LOW) return 2;
  return share <= NICHE_TOP5_CONCENTRATION_MID ? 1 : 0;
}

function scoreReviewsStrength(nd: NicheDataInput): number | null {
  const reviews = num(nd.mainCompetitorReviews);
  if (reviews === null) return null;
  if (reviews < NICHE_REVIEWS_LOW) return 2;
  return reviews <= NICHE_REVIEWS_MID ? 1 : 0;
}

function scoreCompetitorRating(nd: NicheDataInput): number | null {
  const rating = num(nd.mainCompetitorRating);
  if (rating === null) return null;
  if (rating < NICHE_RATING_WEAK) return 2;
  return rating <= NICHE_RATING_MID ? 1 : 0;
}

function scoreReturnRate(nd: NicheDataInput): number | null {
  const rate = num(nd.returnRateT360);
  if (rate === null) return null;
  if (rate < NICHE_RETURN_LOW) return 2;
  return rate <= NICHE_RETURN_MID ? 1 : 0;
}

interface CriterionDef {
  id: NicheCriterionInput['id'];
  block: NicheBlock;
  maxPoint: number;
  score: (nd: NicheDataInput) => number | null;
}

// Порядок фиксирован и совпадает с бэком (parity тестов). 1–12 — проект PLP-23; 13–14 — follow-up.
const NICHE_CRITERIA: readonly CriterionDef[] = [
  { id: 'nicheDemandVolume', block: 'demand', maxPoint: NICHE_MAX_POINT_TERNARY, score: scoreDemandVolume },
  { id: 'nicheRealDemand', block: 'demand', maxPoint: NICHE_MAX_POINT_TERNARY, score: scoreRealDemand },
  { id: 'nicheConversion', block: 'demand', maxPoint: NICHE_MAX_POINT_TERNARY, score: scoreConversion },
  { id: 'nicheDemandGrowth90', block: 'demand', maxPoint: NICHE_MAX_POINT_TERNARY, score: scoreDemandGrowth90 },
  { id: 'nicheYoyTrend', block: 'demand', maxPoint: NICHE_MAX_POINT_BINARY, score: scoreYoyTrend },
  { id: 'nicheActualSales', block: 'demand', maxPoint: NICHE_MAX_POINT_TERNARY, score: scoreActualSales },
  { id: 'nichePriceLevel', block: 'demand', maxPoint: NICHE_MAX_POINT_BINARY, score: scorePriceLevel },
  { id: 'nicheSaturation', block: 'competition', maxPoint: NICHE_MAX_POINT_TERNARY, score: scoreSaturation },
  { id: 'nicheTop5Concentration', block: 'competition', maxPoint: NICHE_MAX_POINT_TERNARY, score: scoreTop5Concentration },
  { id: 'nicheReviewsStrength', block: 'competition', maxPoint: NICHE_MAX_POINT_TERNARY, score: scoreReviewsStrength },
  { id: 'nicheCompetitorRating', block: 'competition', maxPoint: NICHE_MAX_POINT_TERNARY, score: scoreCompetitorRating },
  { id: 'nicheReturnRate', block: 'competition', maxPoint: NICHE_MAX_POINT_TERNARY, score: scoreReturnRate },
];

/**
 * Построить входы niche-критериев из снимка привязанной ниши (PLP-24) для балльной оценки.
 *
 * Для каждого из 12 активных критериев: балл 0/1/2 по порогам проекта → нормировка в `rawScore`
 * 0..1 (÷ максимум критерия) → `NicheCriterionInput` с множителем `NICHE_CRITERION_MULTIPLIER`.
 * Критерии, чьё поле отсутствует/не число, пропускаются (частичная оценка, NO FALLBACKS). Порядок
 * совпадает с бэком.
 */
export function buildNicheCriteria(nicheData: NicheDataInput): NicheCriterionInput[] {
  const result: NicheCriterionInput[] = [];
  for (const def of NICHE_CRITERIA) {
    const point = def.score(nicheData);
    if (point === null) continue;
    result.push({
      id: def.id,
      block: def.block,
      rawScore: point / def.maxPoint,
      multiplier: NICHE_CRITERION_MULTIPLIER,
    });
  }
  return result;
}
